import { type FC } from 'react';

import { CalculateTimepointCell } from '@/components/Samples/SamplePlanTable/CalculateTimepointCell';
import Table from '@/components/UI/Table';
import type { TanStackColumnDef } from '@/components/UI/Table/TableComponent.model';
import { _isEmpty, _isNotBlank } from '@/littledash';
import { DateUtils } from '@/utils/Date.utils';

import type { SuccessfulOverviewSection } from '../../OverviewAccordion.model';
import OverviewGroupChips from '../OverviewGroupChips';
import overviewTableStyles from '../OverviewSectionTable.module.scss';
import styles from './SamplingPlanSectionContent.module.scss';

interface SamplingPlanSectionContentProps {
  section: SuccessfulOverviewSection<'sampling_plan'>;
}

type SamplingPlan = SuccessfulOverviewSection<'sampling_plan'>['rows'][number];

const emptyValue = '-';

const formatTimepoint = (timepoint: string): string =>
  DateUtils.renderDuration(timepoint, { format: 'short', zeros: true, defaultResponse: timepoint });

/**
 * Renders the compact sampling-plan rows behind the Sampling plan accordion section.
 */
const SamplingPlanSectionContent: FC<SamplingPlanSectionContentProps> = ({ section }) => {
  const rows = section.rows;
  const columns: Array<TanStackColumnDef<SamplingPlan>> = [
    {
      id: 'name',
      header: 'Name',
      accessorKey: 'name',
    },
    {
      id: 'group',
      header: 'Group',
      accessorKey: 'target',
      enableSorting: false,
      cell: ({
        row: {
          original: { target },
        },
      }) => <OverviewGroupChips groupApiIds={target.group_api_ids} revealGroupsOnClick />,
    },
    {
      id: 'sample-type',
      header: 'Sample type',
      accessorKey: 'sample_type_name',
      cell: ({
        row: {
          original: { sample_type_name: sampleTypeName },
        },
      }) => (_isNotBlank(sampleTypeName) ? sampleTypeName : emptyValue),
    },
    {
      id: 'timepoints',
      header: 'Timepoints',
      accessorKey: 'timepoints',
      cell: ({
        row: {
          original: { timepoints },
        },
      }) =>
        _isEmpty(timepoints) ? (
          emptyValue
        ) : (
          <div className={styles.timepoints} data-test-element="timepoints">
            {timepoints.map((timepoint) => (
              <span
                key={timepoint}
                className={styles.chip}
                data-test-element="timepoint-chip"
                data-test-key={timepoint}
              >
                {formatTimepoint(timepoint)}
              </span>
            ))}
          </div>
        ),
    },
    {
      id: 'calculate-collection-time',
      header: 'Calculate collection time',
      accessorKey: 'calculate_collection_time',
      cell: ({
        row: {
          original: { calculate_collection_time: calculateCollectionTime },
        },
      }) => <CalculateTimepointCell value={calculateCollectionTime} />,
    },
    {
      id: 'volume',
      header: 'Volume',
      accessorKey: 'volume',
      cell: ({
        row: {
          original: { volume },
        },
      }) => (_isNotBlank(volume) ? volume : emptyValue),
    },
  ];

  return (
    <div data-test-component="SamplingPlanSectionContent" data-test-element="container">
      <Table data={rows} columns={columns} maxHeightClassName={overviewTableStyles.maxHeight} />
    </div>
  );
};

export default SamplingPlanSectionContent;
